(() => {
  if (window.AuraLookbookScriptReady) return;
  window.AuraLookbookScriptReady = true;
  const qs = (selector, root = document) => root.querySelector(selector);
  const qsa = (selector, root = document) => Array.from(root.querySelectorAll(selector));
  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

  class AuraLookbookHeroCarousel {
    constructor(root) {
      this.root = root;
      this.cards = qsa("[data-aura-lookbook-hero-card]", root);
      this.prevBtn = qs("[data-aura-lookbook-prev]", root);
      this.nextBtn = qs("[data-aura-lookbook-next]", root);
      this.scrollBtn = qs("[data-aura-lookbook-scroll]", root);

      this.index = Number(root.dataset.initialIndex || 0);
      this.count = this.cards.length;
      this.infinite = root.dataset.infinite === "true";
      this.autoplay = root.dataset.autoplay === "true";
      this.pauseOnInteraction = root.dataset.pauseOnInteraction === "true";
      this.speed = Math.max(Number(root.dataset.autoplaySpeed || 4200), 1200);
      this.timer = null;

      this.pointerDown = false;
      this.startX = 0;
      this.currentX = 0;
      this.dragDistance = 0;
      this.raf = null;
      this.pointerDownCard = null;
      this.skipNextClick = false;
      this.resizeRaf = null;
      this.preloadedHeroImages = new Set();

      if (!this.count) return;

      this.index = Math.min(Math.max(this.index, 0), this.count - 1);
      this.disableNativeImageDrag();
      this.bind();
      this.update();
      this.startAutoplay();
    }

    isMobile() {
      return window.matchMedia("(max-width: 767px)").matches;
    }

    number(name, fallback) {
      const value = Number(this.root.dataset[name]);
      return Number.isFinite(value) ? value : fallback;
    }

    disableNativeImageDrag() {
      this.cards.forEach((card) => {
        qsa("img", card).forEach((img) => {
          img.setAttribute("draggable", "false");
          img.addEventListener("dragstart", (event) => event.preventDefault());
        });
      });
    }

    getVisibleSideCards() {
      return this.isMobile()
        ? Math.max(this.number("mobileVisibleSideCards", 1), 0)
        : Math.max(this.number("visibleSideCards", 3), 1);
    }

    getSideOffset() {
      return this.isMobile()
        ? Math.max(this.number("mobileSideOffset", 214), 80)
        : Math.max(this.number("sideOffset", 250), 80);
    }

    getCardIndex(card) {
      return this.cards.indexOf(card);
    }

    activateCard(card) {
      const cardIndex = this.getCardIndex(card);
      if (cardIndex > -1 && cardIndex !== this.index) {
        this.userInteracted();
        this.index = cardIndex;
        this.update();
        return true;
      }
      return false;
    }

    bind() {
      if (this.prevBtn) {
        this.prevBtn.addEventListener("click", () => {
          this.userInteracted();
          this.go(-1);
        });
      }

      if (this.nextBtn) {
        this.nextBtn.addEventListener("click", () => {
          this.userInteracted();
          this.go(1);
        });
      }

      if (this.scrollBtn) {
        this.scrollBtn.addEventListener("click", () => {
          this.userInteracted();
          const target =
            document.querySelector(".aura-lookbook-mood") ||
            document.querySelector("[id^='AuraLookbookMood-']");
          if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
        });
      }

      /*
        IMPORTANT FIX:
        The centre card must not visually leave the centre while dragging.
        On mobile we only read the swipe distance and snap on release.
        There are no per-frame transform writes during drag, which keeps the carousel stable and faster.
      */
      this.root.addEventListener("pointerdown", (event) => {
        if (event.target.closest("a, button")) return;
        if (event.pointerType === "mouse" && event.button !== 0) return;

        this.pointerDown = true;
        this.pointerDownCard = event.target.closest("[data-aura-lookbook-hero-card]");
        this.startX = event.clientX;
        this.currentX = event.clientX;
        this.dragDistance = 0;

        this.root.classList.add("is-dragging");
        this.root.style.setProperty("--aura-lookbook-drag-x", "0px");

        try {
          this.root.setPointerCapture(event.pointerId);
        } catch (error) {}

        this.userInteracted();
        this.preloadAround(this.index);
      }, { passive: true });

      this.root.addEventListener("pointermove", (event) => {
        if (!this.pointerDown) return;

        this.currentX = event.clientX;
        this.dragDistance = this.currentX - this.startX;
      }, { passive: true });

      const endDrag = (event) => {
        if (!this.pointerDown) return;

        const moved = Math.abs(this.dragDistance);
        const clickThreshold = this.isMobile() ? 10 : 8;
        const slideThreshold = this.isMobile() ? 26 : 48;
        const cardToActivate = this.pointerDownCard;

        this.pointerDown = false;
        this.pointerDownCard = null;
        this.root.classList.remove("is-dragging");
        this.root.style.setProperty("--aura-lookbook-drag-x", "0px");

        try {
          this.root.releasePointerCapture(event.pointerId);
        } catch (error) {}

        if (moved > slideThreshold) {
          this.skipNextClick = true;
          this.go(this.dragDistance < 0 ? 1 : -1);
          window.setTimeout(() => {
            this.skipNextClick = false;
            this.dragDistance = 0;
          }, 0);
          return;
        }

        if (moved <= clickThreshold && cardToActivate && this.root.contains(cardToActivate)) {
          this.skipNextClick = true;
          this.activateCard(cardToActivate);
          window.setTimeout(() => {
            this.skipNextClick = false;
            this.dragDistance = 0;
          }, 0);
          return;
        }

        window.setTimeout(() => {
          this.dragDistance = 0;
        }, 0);
      };

      this.root.addEventListener("pointerup", endDrag);
      this.root.addEventListener("pointercancel", endDrag);
      window.addEventListener("pointerup", endDrag);

      /*
        Fallback click handler for transformed/overlapped cards.
      */
      this.root.addEventListener("click", (event) => {
        if (this.skipNextClick) return;
        if (Math.abs(this.dragDistance) > 8) return;
        if (event.target.closest("a, button")) return;

        const card = event.target.closest("[data-aura-lookbook-hero-card]");
        if (!card || !this.root.contains(card)) return;

        this.activateCard(card);
      });

      this.root.addEventListener("mouseenter", () => {
        if (this.root.dataset.pauseOnHover === "true") this.stopAutoplay();
      });

      this.root.addEventListener("mouseleave", () => {
        if (this.root.dataset.pauseOnHover === "true" && this.autoplay) this.startAutoplay();
      });

      window.addEventListener("resize", () => {
        if (this.resizeRaf) window.cancelAnimationFrame(this.resizeRaf);
        this.resizeRaf = window.requestAnimationFrame(() => {
          this.resizeRaf = null;
          this.update();
        });
      }, { passive: true });
    }

    userInteracted() {
      if (this.pauseOnInteraction) {
        this.autoplay = false;
        this.stopAutoplay();
      }
    }

    startAutoplay() {
      this.stopAutoplay();
      if (!this.autoplay || this.count < 2) return;

      this.timer = window.setInterval(() => {
        this.go(1, true);
      }, this.speed);
    }

    stopAutoplay() {
      if (this.timer) {
        window.clearInterval(this.timer);
        this.timer = null;
      }
    }

    go(direction, fromAutoplay = false) {
      if (!this.count) return;

      let next = this.index + direction;

      if (this.infinite) {
        next = (next + this.count) % this.count;
      } else {
        next = Math.min(Math.max(next, 0), this.count - 1);
      }

      if (next === this.index && !fromAutoplay) return;

      this.index = next;
      this.update();

      if (!fromAutoplay && this.autoplay) this.startAutoplay();
    }

    getDelta(cardIndex) {
      let delta = cardIndex - this.index;

      if (this.infinite && this.count > 2) {
        const half = this.count / 2;
        if (delta > half) delta -= this.count;
        if (delta < -half) delta += this.count;
      }

      return delta;
    }

    getCardByWrappedIndex(index) {
      if (!this.count) return null;
      const wrapped = this.infinite ? ((index % this.count) + this.count) % this.count : index;
      return this.cards[wrapped] || null;
    }

    preloadCardImage(card) {
      if (!card) return;
      const image = qs("img", card);
      if (!image) return;

      const src = image.currentSrc || image.src || image.getAttribute("src");
      if (!src || this.preloadedHeroImages.has(src)) return;

      this.preloadedHeroImages.add(src);

      try {
        image.decoding = "async";
        if (typeof image.decode === "function" && image.complete) {
          image.decode().catch(() => {});
        }
      } catch (error) {}
    }

    preloadAround(index) {
      this.preloadCardImage(this.getCardByWrappedIndex(index));
      this.preloadCardImage(this.getCardByWrappedIndex(index + 1));
      this.preloadCardImage(this.getCardByWrappedIndex(index - 1));
    }

    update() {
      const mobile = this.isMobile();
      const visibleSide = this.getVisibleSideCards();
      const sideOffset = this.getSideOffset();

      this.cards.forEach((card, cardIndex) => {
        const delta = this.getDelta(cardIndex);
        const absDelta = Math.abs(delta);
        const isActive = delta === 0;
        const isVisible = absDelta <= visibleSide;

        const scale = isActive ? 1 : clamp(0.92 - ((absDelta - 1) * 0.075), 0.58, 0.92);
        const opacity = isActive ? 1 : (isVisible ? clamp(0.92 - ((absDelta - 1) * 0.14), 0.22, 0.92) : 0);
        const z = isActive ? 100 : Math.max(1, 80 - absDelta);
        const x = delta * sideOffset;

        card.classList.toggle("is-active", isActive);
        card.classList.toggle("is-hidden", !isVisible);
        card.classList.toggle("is-side", !isActive && isVisible);

        card.style.setProperty("--aura-card-x", `${x}px`);
        card.style.setProperty("--aura-card-scale", String(scale));
        card.style.setProperty("--aura-card-opacity", String(opacity));
        card.style.setProperty("--aura-card-z", String(z));
        card.style.setProperty(
          "--aura-card-width-current",
          mobile
            ? "var(--aura-focus-mobile-active-card-width, 280px)"
            : (isActive ? "var(--aura-focus-active-card-width, 390px)" : "var(--aura-focus-side-card-width, 245px)")
        );

        card.dataset.auraPosition = String(delta);
        card.setAttribute("aria-hidden", isActive ? "false" : "true");
      });

      this.preloadAround(this.index);

      if (!this.infinite) {
        if (this.prevBtn) this.prevBtn.disabled = this.index === 0;
        if (this.nextBtn) this.nextBtn.disabled = this.index === this.count - 1;
      }
    }

    destroy() {
      this.stopAutoplay();
      if (this.raf) window.cancelAnimationFrame(this.raf);
      if (this.resizeRaf) window.cancelAnimationFrame(this.resizeRaf);
    }
  }


  class AuraLookbookMoodScroller {
    constructor(root) {
      this.root = root;
      this.isDown = false;
      this.startX = 0;
      this.scrollLeft = 0;
      this.moved = false;
      this.bind();
    }

    isMobile() {
      return window.matchMedia("(max-width: 767px)").matches;
    }

    bind() {
      qsa("img", this.root).forEach((img) => {
        img.setAttribute("draggable", "false");
        img.addEventListener("dragstart", (event) => event.preventDefault());
      });

      this.root.addEventListener("pointerdown", (event) => {
        if (!this.isMobile()) return;
        if (event.target.closest("a, button")) return;
        if (event.pointerType === "mouse" && event.button !== 0) return;

        this.isDown = true;
        this.moved = false;
        this.startX = event.clientX;
        this.scrollLeft = this.root.scrollLeft;
        this.root.classList.add("is-dragging");

        try {
          this.root.setPointerCapture(event.pointerId);
        } catch (error) {}
      }, { passive: true });

      this.root.addEventListener("pointermove", (event) => {
        if (!this.isDown || !this.isMobile()) return;

        const walk = event.clientX - this.startX;
        if (Math.abs(walk) > 4) this.moved = true;
        this.root.scrollLeft = this.scrollLeft - walk;
      }, { passive: true });

      const endDrag = (event) => {
        if (!this.isDown) return;

        this.isDown = false;
        this.root.classList.remove("is-dragging");

        try {
          this.root.releasePointerCapture(event.pointerId);
        } catch (error) {}
      };

      this.root.addEventListener("pointerup", endDrag);
      this.root.addEventListener("pointercancel", endDrag);
      this.root.addEventListener("mouseleave", endDrag);

      this.root.addEventListener("click", (event) => {
        if (!this.moved) return;
        event.preventDefault();
        event.stopPropagation();
        this.moved = false;
      }, true);
    }
  }


  class AuraLookbookMoodLightbox {
    constructor(root) {
      this.root = root;
      this.items = qsa("[data-aura-lookbook-lightbox-item]", root);
      this.dialog = qs("[data-aura-lookbook-lightbox-dialog]", root);
      this.image = qs("[data-aura-lookbook-lightbox-image]", root);
      this.caption = qs("[data-aura-lookbook-lightbox-caption]", root);
      this.closeButtons = qsa("[data-aura-lookbook-lightbox-close]", root);
      this.loader = qs("[data-aura-lookbook-lightbox-loader]", root);
      this.isOpen = false;
      this.lastFocus = null;
      this.historyPushed = false;
      this.suppressNextPop = false;
      this.closeTimer = null;
      this.preloadCache = new Map();
      this.activeSrc = "";
      this.loadTick = 0;

      if (!this.items.length || !this.dialog || !this.image) return;

      this.onKeydown = this.onKeydown.bind(this);
      this.onPopState = this.onPopState.bind(this);
      this.bind();
    }

    bind() {
      this.items.forEach((item) => {
        qsa("img", item).forEach((img) => {
          img.setAttribute("draggable", "false");
          img.addEventListener("dragstart", (event) => event.preventDefault());
        });

        item.addEventListener("pointerenter", () => this.preload(item), { passive: true });
        item.addEventListener("focusin", () => this.preload(item));
        item.addEventListener("touchstart", () => this.preload(item), { passive: true });

        item.addEventListener("click", () => this.open(item));
        item.addEventListener("keydown", (event) => {
          if (event.key !== "Enter" && event.key !== " ") return;
          event.preventDefault();
          this.open(item);
        });
      });

      this.closeButtons.forEach((button) => {
        button.addEventListener("click", () => this.close());
      });
    }

    isMobile() {
      return window.matchMedia("(max-width: 767px)").matches;
    }

    getSource(item) {
      const image = qs("img", item);
      const responsiveSrc = this.isMobile()
        ? item.dataset.auraLightboxMobileSrc
        : item.dataset.auraLightboxDesktopSrc;

      return responsiveSrc || item.dataset.auraLightboxSrc || (image ? (image.currentSrc || image.src) : "");
    }

    getAlt(item) {
      const image = qs("img", item);
      return item.dataset.auraLightboxAlt || (image ? image.alt : "") || "Aura lookbook image";
    }

    getCaption(item) {
      return item.dataset.auraLightboxCaption || "";
    }

    preload(item) {
      const src = this.getSource(item);
      if (!src || this.preloadCache.has(src)) return this.preloadCache.get(src);

      const promise = new Promise((resolve, reject) => {
        const img = new Image();
        img.decoding = "async";
        img.onload = () => {
          resolve(src);
        };
        img.onerror = reject;
        img.src = src;
      });

      this.preloadCache.set(src, promise);
      return promise;
    }

    prepareOpen(src, alt, caption) {
      if (this.closeTimer) {
        window.clearTimeout(this.closeTimer);
        this.closeTimer = null;
      }

      this.loadTick += 1;
      this.activeSrc = src;

      this.image.onload = null;
      this.image.onerror = null;
      this.image.removeAttribute("src");
      this.image.alt = alt;

      if (this.caption) this.caption.textContent = caption;

      this.dialog.classList.remove("is-loaded", "is-error", "is-closing");
      this.dialog.classList.add("is-loading");
      if (this.loader) this.loader.setAttribute("aria-hidden", "false");
    }

    revealLoadedImage(tick, src) {
      if (tick !== this.loadTick || src !== this.activeSrc) return;

      this.dialog.classList.remove("is-loading", "is-error");
      this.dialog.classList.add("is-loaded");
      if (this.loader) this.loader.setAttribute("aria-hidden", "true");
    }

    markLoadError(tick, src) {
      if (tick !== this.loadTick || src !== this.activeSrc) return;

      this.dialog.classList.remove("is-loading", "is-loaded");
      this.dialog.classList.add("is-error");
      if (this.loader) this.loader.setAttribute("aria-hidden", "false");
    }

    open(item) {
      const src = this.getSource(item);
      if (!src) return;

      this.lastFocus = document.activeElement;
      const alt = this.getAlt(item);
      const caption = this.getCaption(item);

      this.prepareOpen(src, alt, caption);
      const currentTick = this.loadTick;

      this.dialog.hidden = false;
      this.dialog.setAttribute("aria-hidden", "false");
      document.body.classList.add("aura-lookbook-lightbox-open");

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(() => {
          this.dialog.classList.add("is-open");
        });
      });

      const onLoad = () => {
        const finishReveal = () => this.revealLoadedImage(currentTick, src);

        if (this.image.decode) {
          this.image.decode().then(finishReveal).catch(finishReveal);
        } else {
          finishReveal();
        }
      };

      const onError = () => this.markLoadError(currentTick, src);

      this.image.onload = onLoad;
      this.image.onerror = onError;
      this.image.src = src;

      if (this.image.complete && this.image.naturalWidth > 0) {
        onLoad();
      }

      if (!this.isOpen) {
        document.addEventListener("keydown", this.onKeydown);
        window.addEventListener("popstate", this.onPopState);
      }

      this.isOpen = true;

      try {
        window.history.pushState({ auraLookbookLightbox: true }, "", window.location.href);
        this.historyPushed = true;
      } catch (error) {
        this.historyPushed = false;
      }

      const closeButton = qs(".aura-lookbook-lightbox__close", this.dialog);
      if (closeButton) closeButton.focus({ preventScroll: true });
    }

    close(options = {}) {
      const fromPopState = options.fromPopState === true;
      if (!this.isOpen) return;

      this.isOpen = false;
      this.dialog.classList.remove("is-open");
      this.dialog.classList.add("is-closing");
      this.dialog.setAttribute("aria-hidden", "true");
      document.body.classList.remove("aura-lookbook-lightbox-open");
      document.removeEventListener("keydown", this.onKeydown);

      this.closeTimer = window.setTimeout(() => {
        if (!this.isOpen) {
          this.dialog.hidden = true;
          this.dialog.classList.remove("is-loading", "is-loaded", "is-error", "is-closing");
          this.image.onload = null;
          this.image.onerror = null;
          this.image.removeAttribute("src");
          this.activeSrc = "";
          if (this.caption) this.caption.textContent = "";
          if (this.loader) this.loader.setAttribute("aria-hidden", "true");
        }
      }, 420);

      if (this.lastFocus && typeof this.lastFocus.focus === "function") {
        try {
          this.lastFocus.focus({ preventScroll: true });
        } catch (error) {}
      }

      if (!fromPopState && this.historyPushed) {
        this.historyPushed = false;
        this.suppressNextPop = true;
        try {
          window.history.back();
          window.setTimeout(() => {
            this.suppressNextPop = false;
          }, 140);
        } catch (error) {
          this.suppressNextPop = false;
        }
      } else {
        this.historyPushed = false;
      }
    }

    onKeydown(event) {
      if (event.key === "Escape") {
        event.preventDefault();
        this.close();
      }
    }

    onPopState() {
      if (this.suppressNextPop) {
        this.suppressNextPop = false;
        return;
      }

      if (this.isOpen) {
        this.close({ fromPopState: true });
      }
    }

    destroy() {
      if (this.isOpen) this.close({ fromPopState: true });
      document.removeEventListener("keydown", this.onKeydown);
      window.removeEventListener("popstate", this.onPopState);
      if (this.closeTimer) window.clearTimeout(this.closeTimer);
      this.image.onload = null;
      this.image.onerror = null;
    }
  }

  const init = (root = document) => {
    /* Mood grid layout is CSS-only from v6 onward. V8 adds optimised scoped lightbox preview. */
    qsa("[data-aura-lookbook-mood-lightbox]", root).forEach((section) => {
      if (section.dataset.auraMoodLightboxReady === "true") return;
      section.dataset.auraMoodLightboxReady = "true";
      section.auraMoodLightbox = new AuraLookbookMoodLightbox(section);
    });

    qsa("[data-aura-lookbook-hero-carousel]", root).forEach((carousel) => {
      if (carousel.dataset.auraReady === "true") return;
      carousel.dataset.auraReady = "true";
      carousel.auraCarousel = new AuraLookbookHeroCarousel(carousel);
    });
  };

  document.addEventListener("DOMContentLoaded", () => init());

  document.addEventListener("shopify:section:load", (event) => {
    init(event.target);
  });

  document.addEventListener("shopify:section:unload", (event) => {
    qsa("[data-aura-lookbook-mood-lightbox]", event.target).forEach((section) => {
      if (section.auraMoodLightbox) section.auraMoodLightbox.destroy();
    });

    qsa("[data-aura-lookbook-hero-carousel]", event.target).forEach((carousel) => {
      if (carousel.auraCarousel) carousel.auraCarousel.destroy();
    });
  });
})();
